import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { STATUS_LABEL } from "../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const STATUS_CLS = {
  not_started: "bg-stone-100 text-stone-400 border-stone-200",
  learning: "bg-amber-50 text-amber-700 border-amber-300",
  needs_practice: "bg-red-50 text-red-700 border-red-300",
  mastered: "bg-green-50 text-green-700 border-green-400",
};

const Stat = ({ label, value, sub, testid }) => (
  <div className="scroll-card p-5" data-testid={testid}>
    <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
    <div className="text-4xl font-bold text-[var(--tea)] mt-2">{value}<span className="text-lg text-stone-400 font-semibold">{sub}</span></div>
  </div>
);

const Bar100 = ({ value, total, color }) => (
  <div className="h-3 rounded-full bg-stone-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${total ? (value / total) * 100 : 0}%`, background: color }} /></div>
);

export const OverallSummary = ({ d }) => {
  const s = d.summary;
  return (
    <div className="space-y-6" data-testid="overall-summary">
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <Stat testid="summary-stories" label="Stories Completed (all halls)" value={s.stories_completed} sub={` / ${s.stories_total}`} />
        <Stat testid="summary-week" label="Learning Time This Week" value={s.week_minutes} sub=" min" />
        <Stat testid="summary-stars" label="Stars" value={s.stars} />
        <Stat testid="summary-badges" label="Badges" value={s.badges} />
      </section>
      <section className="grid lg:grid-cols-[1fr_1fr] gap-6">
        <div className="scroll-card p-6 space-y-5" data-testid="summary-halls">
          <h2 className="text-lg font-bold">By hall</h2>
          {s.halls.map((h) => (
            <div key={h.hall} data-testid={`summary-hall-${h.hall}`}>
              <div className="flex justify-between text-sm mb-1.5"><span className="font-serif-tc font-bold text-base">{h.name}</span>
                <span className="text-stone-500">{h.completed} / {h.total} stories · {h.week_minutes} min this week</span></div>
              <Bar100 value={h.completed} total={h.total} color={{ life: "#3E9C8F", speak: "#D9573B" }[h.hall] || "#C83E2B"} />
            </div>
          ))}
          <div className="text-sm text-stone-500">Quiz accuracy overall: <b>{s.quiz_accuracy == null ? "—" : `${Math.round(s.quiz_accuracy * 100)}%`}</b> · Total learning: <b>{s.total_minutes} min</b></div>
        </div>
        <div className="scroll-card p-6">
          <h2 className="text-lg font-bold mb-4">Daily minutes (last 7 days)</h2>
          <div className="h-52">
            <ResponsiveContainer>
              <BarChart data={d.daily_minutes.map((x) => ({ ...x, day: x.date.slice(5) }))}>
                <XAxis dataKey="day" fontSize={12} /><YAxis fontSize={12} allowDecimals={false} /><Tooltip />
                <Bar dataKey="minutes" fill="#C83E2B" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>
    </div>
  );
};

export const LifeProgress = ({ d }) => {
  const L = d.life;
  const weak = d.recommended.words || [];
  return (
    <div className="space-y-6" data-testid="life-progress">
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <Stat testid="life-stat-stories" label="Life Stories Completed" value={L.stories_completed} sub={` / ${L.stories_total}`} />
        <Stat testid="life-stat-words" label="Words Learned" value={L.words_learned} sub={` / ${L.words_total}`} />
        <Stat testid="life-stat-patterns" label="Sentence Patterns Practiced" value={L.patterns_practiced} sub={` / ${L.patterns_total}`} />
        <Stat testid="life-stat-week" label="Life Hall Time This Week" value={L.week_minutes} sub=" min" />
      </section>
      <section className="grid lg:grid-cols-[1fr_1fr] gap-6">
        <div className="scroll-card p-6 space-y-4" data-testid="life-theme-progress">
          <h2 className="text-lg font-bold">Themes</h2>
          {L.themes.map((t) => (
            <div key={t.theme_id} data-testid={`life-dash-theme-${t.theme_id}`}>
              <div className="flex justify-between text-sm mb-1"><span className="font-round text-base">{t.emoji} {t.name}</span><span className="text-stone-500">{t.completed} / {t.total}</span></div>
              <Bar100 value={t.completed} total={t.total} color="#3E9C8F" />
            </div>
          ))}
        </div>
        <div className="space-y-6">
          <div className="scroll-card p-6" data-testid="life-recommended">
            <h2 className="text-lg font-bold">Recommended Review — Words</h2>
            <div className="flex flex-wrap gap-2 mt-3">{weak.length ? weak.map((w) => <span key={w} className="px-3 h-11 rounded-xl bg-red-50 border-2 border-red-200 font-round text-xl flex items-center">{w}</span>) : <span className="text-sm text-stone-400">Nothing to review yet</span>}</div>
          </div>
          <div className="scroll-card p-6" data-testid="life-lessons-list">
            <h2 className="text-lg font-bold mb-3">Life Stories</h2>
            <div className="space-y-1.5 max-h-72 overflow-auto">
              {L.lessons.map((l) => (
                <div key={l.story_id} className="flex items-center justify-between text-sm">
                  <span className="font-round text-base">{l.title}</span>
                  <span className={l.status === "completed" ? "text-green-700 font-semibold" : l.status === "in_progress" ? "text-amber-700" : "text-stone-400"}>
                    {l.status === "completed" ? "★".repeat(l.stars) : l.status === "in_progress" ? "In progress" : "—"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="scroll-card p-6 overflow-hidden" data-testid="life-word-table">
        <h2 className="text-lg font-bold mb-3">Word Progress</h2>
        <div className="max-h-[420px] overflow-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Word</TableHead><TableHead>Zhuyin</TableHead><TableHead>Meaning</TableHead><TableHead>Accuracy</TableHead><TableHead>Practice</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {L.words.map((w) => (
                <TableRow key={w.word}>
                  <TableCell className="font-round text-xl">{w.emoji} {w.word}</TableCell>
                  <TableCell>{w.zhuyin}</TableCell>
                  <TableCell className="text-stone-500 text-sm">{w.meaning}</TableCell>
                  <TableCell>{w.accuracy == null ? "—" : `${Math.round(w.accuracy * 100)}%`}</TableCell>
                  <TableCell>{w.practice_count}</TableCell>
                  <TableCell><span className={`px-2 py-1 rounded-full border text-xs ${STATUS_CLS[w.status]}`}>{STATUS_LABEL[w.status]}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
};
